import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Divider from "@mui/material/Divider";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { setDeviceData } from "../store/deviceSlice";
import {
    GetDateTimeStringToday,
    getUrlVars,
    GetMachineTypeIcon,
    LatestDataColoring,
} from "../utils/systemInfo";
import DevicesService from "../api/devices";
import Loading from "../common/loading";
import StyledTypography from "../common/StyledTypography";
import CustomTextField from "../common/CustomTextField";
import handleNavigate from "../common/customFunctions/handleNavigate";
import Breadcrumbs from "../common/breadcrumbs";
import { showMessage } from "../store/messageSlice";

const StyledLoadingButton = styled((props) => <LoadingButton {...props} />)(
    ({ theme }) => ({
        backgroundColor: theme.palette.custom.buttonColor,
        width: "10%",
        "&.MuiLoadingButton-loading": {
            backgroundColor: theme.palette.custom.light,
        },
    })
);

function AddEditMachine() {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const organziation = useSelector(({ organziation }) => organziation);
    const device = useSelector(({ device }) => device);
    const [deviceName, setDeviceName] = useState("");
    const [deviceOem, setDeviceOem] = useState(undefined);
    const [deviceSerial, setDeviceSerial] = useState(undefined);
    const [deviceMachineType, setDeviceMachineType] = useState(undefined);
    const [deviceType, setDeviceType] = useState(undefined);
    const [deviceTypeBackend, setDeviceTypeBackend] = useState(undefined);
    const [deviceId, setDeviceId] = useState(undefined);
    const [deviceProfileId, setDeviceProfileId] = useState(undefined);
    const [deviceTypeIcon, setDeviceTypeIcon] = useState(undefined);
    const [deviceLastSeen, setDeviceLastSeen] = useState(undefined);
    const [manufacturerSN, setManufacturerSN] = useState(undefined);
    const [savingLoading, setSavingLoading] = useState(false);
    const [loadingValue, setLoadingValue] = useState(true);
    const DeviceId = String(getUrlVars()["device"]);
    const DeviceTitleDataName = String(getUrlVars()["DeviceTitleDataName"]);
    const timezoneMins = useSelector(({ timezone }) => timezone);

    function getDeviceBreadcrumbs({ DeviceTitleDataName, device }) {
        const urlVars = getUrlVars();
        const deviceId =
            urlVars.device || device?.DeviceID || device?.device_uid || "";
        const deviceName = device?.DeviceName
            ? `${device.DeviceName}`
            : device?.name
                ? `${device.name}`
                : device?.DeviceID || device?.device_uid || "";

        if (DeviceTitleDataName?.toLowerCase() === "device") {
            return [
                { name: "Home", href: "/", noTranslation: true },
                { name: "Devices", href: "/devices", noTranslation: true },
                deviceName
                    ? {
                        name: deviceName,
                        href: `/device?device=${deviceId}&DeviceTitleDataName=Device`,
                        noTranslation: true,
                    }
                    : null,
                { name: "Add/Edit Device", href: "#", noTranslation: true },
            ].filter(Boolean);
        }
        if (DeviceTitleDataName?.toLowerCase() === "machine") {
            return [
                { name: "Home", href: "/", noTranslation: true },
                { name: "Machines", href: "/machines", noTranslation: true },
                deviceName
                    ? {
                        name: deviceName,
                        href: `/device?device=${deviceId}&DeviceTitleDataName=Machine`,
                        noTranslation: true,
                    }
                    : null,
                { name: "Add/Edit Machine", href: "#", noTranslation: true },
            ].filter(Boolean);
        }
    }

    const breadcrumbs = getDeviceBreadcrumbs({ DeviceTitleDataName, device });
    useEffect(() => {
        if (!DeviceId) {
            setLoadingValue(false);
            return;
        }

        const orgObj =
            organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
        if (!orgObj) {
            setLoadingValue(false);
            return;
        }

        const isNewDevice = !device || (device && device.DeviceID !== DeviceId);

        const clearFields = () => {
            setDeviceName(undefined);
            setDeviceOem(undefined);
            setDeviceSerial(undefined);
            setDeviceMachineType(undefined);
            setDeviceTypeBackend(undefined);
            setDeviceType(undefined);
            setDeviceId(undefined);
            setDeviceProfileId(undefined);
            setDeviceTypeIcon(undefined);
            setDeviceLastSeen(undefined);
            setManufacturerSN(undefined);
        };

        const populateFields = (d, isMachine = false) => {
            setDeviceName(isMachine ? d.name : d.DeviceName);
            setDeviceOem(isMachine ? d.oem : d.DeviceOEM);
            setDeviceSerial(isMachine ? d.serial : d.DeviceSerial);
            setDeviceMachineType(
                String(
                    isMachine
                        ? d.vehicle_type !== ""
                            ? d.vehicle_type
                            : d.device_type
                        : d.DeviceType !== ""
                            ? d.DeviceType
                            : d.DeviceType
                )
            );
            setDeviceTypeBackend(isMachine ? d.machine_type : d.MachineType);
            setDeviceId(isMachine ? d.device_uid : d.DeviceID);
            setDeviceProfileId(isMachine ? d.device_profile_id : d.DeviceProfileID);
            setDeviceType(
                String(
                    isMachine
                        ? d.vehicle_type !== ""
                            ? d.vehicle_type
                            : d.device_type
                        : d.DeviceType !== ""
                            ? d.DeviceType
                            : d.DeviceType
                )
            );
            setManufacturerSN(isMachine ? d.manufacturer_sn : d.ManufacturerSN);
            let type = String(
                isMachine
                    ? d.vehicle_type !== ""
                        ? d.vehicle_type
                        : d.device_type
                    : d.DeviceType !== ""
                        ? d.DeviceType
                        : d.DeviceType
            );
            if (!type) type = "Unknown";
            setDeviceTypeIcon(GetMachineTypeIcon(type));
            let lastSeen = isMachine ? d.dst_last_seen : d.DeviceLastSeen;
            if (new Date(lastSeen).getFullYear() < 1900 || !lastSeen?.length) {
                lastSeen = isMachine ? d.latest_data_dst : d.DeviceLastSeen;
                if (new Date(lastSeen).getFullYear() < 1900 || !lastSeen?.length)
                    lastSeen = " not seen";
            }
            setDeviceLastSeen(lastSeen);
        };
        const fetchAndPopulate = async () => {
            setLoadingValue(true);
            if (isNewDevice) {
                clearFields();
                const apiKey = orgObj?.api_key_list?.[0]?.key;
                let params = {};
                let DevicesClass;
                switch (DeviceTitleDataName.toLowerCase()) {
                    case "device":
                        DevicesClass = { deviceType: "", source: "iot" };
                        params = {
                            DeviceID: Number(DeviceId),
                            DeviceType: DevicesClass.deviceType,
                            Source: DevicesClass.source,
                            Live: true,
                            History: true,
                            apiKey,
                            historyFromDST: "",
                            historyToDST: GetDateTimeStringToday(),
                        };
                        break;
                    case "machine":
                    default:
                        DevicesClass = {
                            deviceType: [
                                "nerohub",
                                "nanohub",
                                "cyberhub",
                                "stratahub",
                                "nerotrax",
                                "train",
                                "autonomous hub",
                            ],
                            source: "",
                        };
                        params = {
                            deviceID: Number(DeviceId),
                            deviceType: DevicesClass.deviceType,
                            source: DevicesClass.source,
                            apiKey,
                            historyFromDST: "",
                            historyToDST: GetDateTimeStringToday(),
                        };
                        break;
                }

                try {
                    const json = await DevicesService.getDevices(params);
                    // console.log(json)
                    if (json && json.device_profile && json.device_profile.length > 0) {
                        const deviceObject = json.device_profile.find(
                            (x) => x.device_uid.toString() === DeviceId
                        );
                        if (deviceObject) {
                            // console.log("device", deviceObject);
                            populateFields(deviceObject, true);
                            let type =
                                deviceObject.vehicle_type ||
                                deviceObject.device_type ||
                                "Unknown";
                            const icon = GetMachineTypeIcon(type);
                            setDeviceTypeIcon(icon);
                            dispatch(
                                setDeviceData({
                                    json: deviceObject,
                                    machineTypeFilter: "",
                                    machineType: DeviceTitleDataName,
                                })
                            );
                        }
                    }
                } catch (error) {
                    if (process.env.NODE_ENV === "development")
                        console.log("Device fetch error", error);
                }
            } else {
                if (device) {
                    // console.log("machine", device);
                    populateFields(device, false);
                    dispatch(
                        setDeviceData({
                            json: device,
                            machineTypeFilter: "",
                            machineType: DeviceTitleDataName,
                        })
                    );
                }
            }
            setLoadingValue(false);
        };

        fetchAndPopulate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DeviceId, organziation, DeviceTitleDataName]);

    const handleSubmit = (event) => {
        const orgObj =
            organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
        const apiKey = orgObj?.api_key_list?.[0]?.key;
        setSavingLoading(true);
        event.preventDefault();
        if (deviceId) {
            DevicesService.SetDeviceProfile({
                device_profile_id: deviceProfileId,
                device_uid: deviceId,
                serial:
                    deviceType === "nerohub" ? parseInt(deviceSerial, 16) : deviceSerial,
                name: deviceName,
                oem: deviceOem,
                device_type: deviceMachineType,
                nerohub: "",
                device_type_id: "",
                device_oem_operation_profile_uid: "",
                machine_type: deviceTypeBackend,
                manufacturer_sn: manufacturerSN,
                apiKey,
            })
                .then(() => {
                    dispatch(
                        showMessage({
                            message: t("saved Successfully"), //text or html
                            autoHideDuration: 10000, //ms
                            anchorOrigin: {
                                vertical: "top", //top bottom
                                horizontal: "center", //left center right
                            },
                            variant: "success", //success error info warning null
                        })
                    );
                    navigate(
                        `/device?device=${DeviceId}&DeviceTitleDataName=${DeviceTitleDataName}`
                    );
                })
                .catch((errorr) => {
                    dispatch(
                        showMessage({
                            message: t("error occurred"), //text or html
                            autoHideDuration: 10000, //ms
                            anchorOrigin: {
                                vertical: "top", //top bottom
                                horizontal: "center", //left center right
                            },
                            variant: "error", //success error info warning null
                        })
                    );
                    if (process.env.NODE_ENV === "development")
                        console.log("getUserTypePermissionserrorr", errorr);
                });
        }
        setSavingLoading(false);
    };
    const handleReturn = (event) => {
        handleNavigate(
            event,
            navigate,
            `/device?device=${DeviceId}&DeviceTitleDataName=${DeviceTitleDataName}`
        );
        // navigate();
    };
    return (
        <Box
            sx={{
                p: "12px",
                minHeight: "90vh",
                height: "fit-content",
            }}
        >
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "block", sm: "center" },
                    height: "64px",
                }}
            >
                <IconButton
                    size="small"
                    sx={{
                        backgroundColor: theme.palette.custom.borderColor,
                        color: theme.palette.custom.text,
                        mr: 4,
                        width: "40px",
                        height: "40px",
                    }}
                    onClick={handleReturn}
                >
                    <ArrowBackOutlinedIcon fontSize="inherit" />
                </IconButton>
                <Box sx={{ display: { xs: "block", sm: "flex" }, width: "100%" }}>
                    {deviceTypeIcon && (
                        <Box
                            sx={{
                                textAlign: "center",
                                width: "30%",
                                display: "flex",
                                flexFlow: "row",
                            }}
                        >
                            <div
                                style={{ width: "70px" }}
                                dangerouslySetInnerHTML={{
                                    __html: deviceTypeIcon.replace(
                                        "theme.palette.custom.text",
                                        theme.palette.custom.text
                                    ),
                                }}
                            />{" "}
                            <StyledTypography
                                sx={{ ml: "8px", color: theme.palette.custom.text }}
                                variant="h4"
                            >
                                {deviceName}
                            </StyledTypography>{" "}
                        </Box>
                    )}

                    <Box
                        sx={{
                            textAlign: "center",
                            width: "70%",
                            display: "flex",
                            flexFlow: "row",
                        }}
                    >
                        <StyledTypography
                            sx={{ color: theme.palette.custom.text }}
                            variant="h4"
                        >
                            {`${t("LastSeen")}:`}
                        </StyledTypography>{" "}
                        <Typography
                            sx={{
                                ml: "8px",
                                color: LatestDataColoring(deviceLastSeen, true),
                                display: "flex",
                                flexFlow: "row",
                                alignItems: "center",
                            }}
                            variant="h4"
                        >
                            {deviceLastSeen !== "Not set" && deviceLastSeen !== " not seen"
                                ? moment(deviceLastSeen)
                                    .add(timezoneMins, "minutes")
                                    .format("YYYY-MM-DD HH:mm")
                                : deviceLastSeen}
                        </Typography>{" "}
                    </Box>
                </Box>
            </Box>
            <Divider
                sx={{ my: "10px", backgroundColor: theme.palette.custom.borderColor }}
            />
            {!loadingValue ? (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                        m: 2,
                        pb: 1,
                        textAlign: "center",
                        display: "flex",
                        flexWrap: "wrap",
                    }}
                >
                    <CustomTextField
                        id="deviceName"
                        label={t("deviceName")}
                        variant="filled"
                        value={deviceName}
                        defaultValue={deviceName}
                        onChange={(event) => {
                            setDeviceName(event.target.value);
                        }}
                        required
                        focused
                        inputProps={{ maxLength: 32 }}
                        sx={{
                            width: { xs: "100%", sm: "48%" },
                            mr: { xs: "0%", sm: "1%" },
                            mb: 1,
                            borderColor: theme.palette.custom.borderColor,
                            borderRadius: "11px",
                            backgroundColor: theme.palette.custom.light,
                        }}
                    />
                    <CustomTextField
                        id="deviceOem"
                        label={t("deviceOem")}
                        name="deviceOem"
                        variant="filled"
                        value={deviceOem}
                        onChange={(event) => {
                            setDeviceOem(event.target.value);
                        }}
                        required
                        focused
                        inputProps={{ maxLength: 32 }}
                        sx={{
                            width: { xs: "100%", sm: "48%" },
                            mb: 1,
                            borderColor: theme.palette.custom.borderColor,
                            borderRadius: "11px",
                            backgroundColor: theme.palette.custom.light,
                        }}
                    />
                    <CustomTextField
                        id="serial"
                        label={t("SerialNumber")}
                        name="serial"
                        autoComplete="string"
                        variant="filled"
                        value={deviceSerial}
                        onChange={(event) => {
                            setDeviceSerial(event.target.value);
                        }}
                        required
                        focused
                        inputProps={{ maxLength: 32 }}
                        sx={{
                            width: { xs: "100%", sm: "48%" },
                            mr: { xs: "0%", sm: "1%" },
                            mb: 1,
                            borderColor: theme.palette.custom.borderColor,
                            borderRadius: "11px",
                            backgroundColor: theme.palette.custom.light,
                        }}
                    />
                    <CustomTextField
                        label={t("deviceType")}
                        name="deviceType"
                        variant="filled"
                        id="deviceType"
                        value={deviceMachineType}
                        onChange={(event) => {
                            setDeviceMachineType(event.target.value);
                        }}
                        required
                        focused
                        inputProps={{ maxLength: 32 }}
                        sx={{
                            width: { xs: "100%", sm: "48%" },
                            mb: 1,
                            borderColor: theme.palette.custom.borderColor,
                            borderRadius: "11px",
                            backgroundColor: theme.palette.custom.light,
                        }}
                    />
                    <CustomTextField
                        id="machineType"
                        label={t("machineType")}
                        name="machineType"
                        autoComplete="string"
                        variant="filled"
                        value={deviceTypeBackend}
                        defaultValue={deviceTypeBackend}
                        onChange={(event) => {
                            setDeviceTypeBackend(event.target.value);
                        }}
                        required
                        focused
                        inputProps={{ maxLength: 32 }}
                        sx={{
                            width: { xs: "100%", sm: "48%" },
                            mr: { xs: "0%", sm: "1%" },
                            borderColor: theme.palette.custom.borderColor,
                            borderRadius: "11px",
                            backgroundColor: theme.palette.custom.light,
                        }}
                    />
                    <CustomTextField
                        id="manufacturerSN"
                        label={t("manufacturerSN")}
                        name="manufacturerSN"
                        autoComplete="string"
                        variant="filled"
                        value={manufacturerSN}
                        defaultValue={manufacturerSN}
                        onChange={(event) => {
                            setManufacturerSN(event.target.value);
                        }}
                        required
                        focused
                        inputProps={{ maxLength: 32 }}
                        sx={{
                            width: { xs: "100%", sm: "48%" },
                            mr: { xs: "0%", sm: "1%" },
                            borderColor: theme.palette.custom.borderColor,
                            borderRadius: "11px",
                            backgroundColor: theme.palette.custom.light,
                        }}
                    />
                </Box>
            ) : (
                <Box sx={{ height: "30vh", pt: "10%" }}>
                    <Loading />
                </Box>
            )}
            <Box sx={{ width: "100%", alignItems: "end", textAlign: "end" }}>
                <StyledLoadingButton
                    loading={savingLoading}
                    loadingIndicator={<CircularProgress color="primary" size={16} />}
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 1 }}
                    onClick={handleSubmit}
                >
                    {t("Save")}
                </StyledLoadingButton>
            </Box>
        </Box>
    );
}

export default AddEditMachine;
